import React, { useState, useRef, useEffect, useCallback, useMemo, useReducer } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useLocation, useNavigate } from "react-router-dom";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { UploaderV4 } from "./Uploaderv4";
import { DocumentImageOptimized } from "../documents/utils";
import { TWclassNames } from "../../utils/Div";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import { ProjectFolderSelector } from "../../utils/moduleSelectors/projectFolderSelector";
import { DrawingSelector } from "../../utils/moduleSelectors/drawingSelector";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import axios from "../../../api/axios";
import {
  bindUploadEntryToFolder,
  folderFromSelectorEvent,
  folderUploadFallback,
} from "./document-upload-service";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import {
  CameraIcon,
  CheckCircleIcon,
  XMarkIcon,
  FolderIcon,
  PhotoIcon,
  DocumentDuplicateIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import ImageViewer from "react-simple-image-viewer";
import { EditDocumentDialog } from "./editDocumentDialog";
import ModernReportSelector from "../../utils/Selector/ModernReportSelector";
import ModernProjectSelector from "../../utils/Selector/ModernProjectSelector";
import { CaseSelector } from "../../utils/moduleSelectors/caseSelector";
import { PMPagination } from "../../utils/PMPagination";
import {
  getDocumentUploadDestinationLabel,
  useDocumentUploadQueueOptional,
} from "../../../contexts/document-upload-queue-context";
import { prepareUploadEntries } from "./documentUploadDrawingUtils";
import {
  DocumentUploadPreviewDialog,
  DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX,
} from "./DocumentUploadPreviewDialog";
import {
  consumeDocumentPlacementRestore,
  requestFullCanvasDocumentPlacement,
  getDocumentPlacementSession,
  shouldSkipAutoOpenPlacement,
  subscribeDocumentPlacement,
} from "../drawings/drawingDocumentPlacementSession";
import {
  locationToPath,
  sanitizeReturnPath,
} from "../../../utils/authReturnUrl";

/** Stable default for optional array props — `= []` in params creates a new reference every render and breaks useCallback/useEffect deps. */
const EMPTY_PROPS_ARRAY = Object.freeze([]);

/** Full viewport height on mobile only; desktop keeps normal modal width. */
const DOCUMENT_MANAGER_MOBILE_HEIGHT_PANEL =
  "!h-[100dvh] !max-h-[100dvh] !min-h-[100dvh] max-sm:w-full max-sm:max-w-full max-sm:rounded-none sm:!h-[720px] sm:!min-h-0 sm:!max-h-[80vh] sm:rounded-2xl";

const DOCUMENT_MANAGER_COMPACT_PANEL = `${DOCUMENT_MANAGER_MOBILE_HEIGHT_PANEL} flex flex-col sm:max-w-4xl md:max-w-4xl lg:max-w-4xl`;

/**
 * DocumentManagerV6 - Komplett dokument/bilde opplaster og viser
 *
 * Features:
 * - Vise eksisterende bilder/dokumenter
 * - Laste opp nye bilder (drag-and-drop, paste, file picker)
 * - Kopiere bilder fra andre moduler
 * - Flytte bilder fra andre moduler
 * - Spesialtilpasset for sjekkliste (kan vise bilder fra rapport og sjekkliste items)
 * - Rask og effektiv visning og opplasting
 *
 * Props:
 * @param {string} module - Modulnavn (f.eks. "project", "checklist_item", "report")
 * @param {string|number} moduleParam - Modul parameter (f.eks. projectId, checklistItemId, reportId)
 * @param {Array} documents - Eksisterende dokumenter (optional, kan hentes automatisk)
 * @param {Function} onDocumentsChange - Callback når dokumenter endres
 * @param {boolean} showUpload - Vis opplastingsområde (default: true)
 * @param {boolean} showExisting - Vis eksisterende dokumenter (default: true)
 * @param {string} mode - "grid" | "list" | "compact" | "inline" | "dropzone"
 * @param {number} maxDisplay - Maks antall dokumenter å vise før "vis alle" (default: 10)
 * @param {boolean} allowCopy - Tillat kopiering fra andre moduler (default: true)
 * @param {boolean} allowMove - Tillat flytting fra andre moduler (default: true)
 * @param {boolean} allowDelete - Tillat sletting (default: true)
 * @param {boolean} allowEdit - Tillat redigering (default: true)
 * @param {string} filterModule - Filtrer dokumenter fra spesifikk modul (for sjekkliste: "report" eller "checklist_item")
 * @param {Array} filterModuleParams - Filtrer dokumenter fra spesifikke modul parametere
 * @param {boolean} autoLoad - Last dokumenter automatisk (default: true)
 * @param {boolean} folderSupport - Støtt mappestruktur (default: false)
 * @param {object} selectedFolder - Valgt mappe (for prosjekter)
 * @param {Function} onFolderChange - Callback når mappe endres
 * @param {string} title - Egendefinert tittel (hvis ikke spesifisert, brukes "Dokumenter")
 * @param {string} subtitle - Undertittel som viser kontekst (automatisk generert hvis ikke spesifisert)
 */
export const DocumentManagerV6 = (props) => {
  const {
    module,
    moduleParam,
    documents: propsDocuments = EMPTY_PROPS_ARRAY,
    onDocumentsChange,
    showUpload = true,
    showExisting = true,
    mode = "grid", // "grid" | "list" | "compact" | "inline" | "dropzone"
    maxDisplay = 10,
    allowCopy = true,
    allowMove = true,
    allowDelete = true,
    allowEdit = true,
    filterModule = null, // For sjekkliste: kan være "report" eller "checklist_item"
    filterModuleParams = EMPTY_PROPS_ARRAY, // Array av module params å filtrere på
    checklistId = null, // ID til sjekklisten (for filtrering av checklist_item)
    checklistItemIds = EMPTY_PROPS_ARRAY, // Array av checklist item IDs (for filtrering av checklist_item)
    autoLoad = true,
    /** When true, loadDocuments also calls onDocumentsChange (offer attachments). Default false. */
    syncDocumentsToParent = false,
    folderSupport = false,
    selectedFolder = null,
    onFolderChange,
    disabled = false,
    title, // Egendefinert tittel
    subtitle, // Egendefinert undertittel
    project, // Project object - when provided, drawing selector will be shown
    defaultDrawing = null, // Pre-selected / linked drawing for checklist → tegning
    lockDrawing = false,
    autoOpenPositionPicker = false,
    enableSketch = false,
  } = props;

  const { t } = useTranslation();
  const documentUploadQueue = useDocumentUploadQueueOptional();
  // Stabilize array prop identity — callers often pass `[reportId]` inline, which would
  // recreate loadDocumentCount and refetch for every parent keystroke/re-render.
  const filterModuleParamsKey = Array.isArray(filterModuleParams)
    ? filterModuleParams.join(",")
    : "";
  const [documents, setDocuments] = useState(propsDocuments || []);
  const [isLoading, setIsLoading] = useState(false);
  const [documentCount, setDocumentCount] = useState(0); // Forhåndslastet dokumentstatus
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [copyMoveDialogOpen, setCopyMoveDialogOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [compactDialogOpen, setCompactDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("view"); // "view" | "upload" | "copyMove"
  const hasLoadedOnDialogOpen = useRef(false);
  const prevModuleRef = useRef({ module, moduleParam, filterModule, filterModuleParams });

  // Upload state
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const fileInputRef = useRef(null);
  const uploadFunctionRef = useRef(null);
  /** Forces re-render after UploaderV4 registers uploadFn (refs do not trigger updates). */
  const [, bumpUploadFnReady] = useReducer((x) => x + 1, 0);
  const [resetUploader, setResetUploader] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(selectedFolder || null);

  const folderProjectId =
    module === "project" ? moduleParam : project?.id || null;
  const { module: fallbackModule, moduleParam: fallbackParam } =
    folderUploadFallback(module, moduleParam, folderProjectId);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropZoneRef = useRef(null);

  const allowedFileTypes =
    props.allowedFileTypes ||
    "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";
  
    const displaySubtitle = subtitle


  // Sync folder state with prop changes
  useEffect(() => {
    setCurrentFolder(selectedFolder || null);
  }, [selectedFolder?.id]);

  // Load document count only (for status display)
  const loadDocumentCount = useCallback(async () => {
    if (!module || !moduleParam) return;

    setIsLoadingCount(true);
    try {
      const params = {
        module: module,
        module_param: moduleParam,
        fields: "id", // Only fetch IDs to get count
        per_page: 1, // Minimal data
      };

      // Add filter params if specified (for sjekkliste)
      if (filterModule && filterModuleParamsKey) {
        params.filter_module = filterModule;
        params.filter_module_params = filterModuleParamsKey;
      }

      const response = await axios.get(`/documents`, { params });
      // API returns totalCount (not total)
      const count = response.data.totalCount || response.data.total || response.data.documents?.length || 0;
      setDocumentCount(count);
      // Also set totalCount so it's available when dialog opens
      setTotalCount(count);
    } catch (error) {
      console.error("Error loading document count:", error);
      setDocumentCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  }, [module, moduleParam, filterModule, filterModuleParamsKey]);

  // Preload total document count for mini / dropzone badge (paged API loads on dialog open)
  useEffect(() => {
    if (module && moduleParam && (mode === "dropzone" || mode === "mini")) {
      loadDocumentCount();
    } else if (mode !== "dropzone" && mode !== "mini") {
      setDocumentCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, moduleParam, mode, filterModule, filterModuleParamsKey]);

  // Reset page when module or moduleParam changes
  useEffect(() => {
    const prevModule = prevModuleRef.current;
    const prevKey = Array.isArray(prevModule.filterModuleParams)
      ? prevModule.filterModuleParams.join(",")
      : "";
    const moduleChanged =
      prevModule.module !== module ||
      prevModule.moduleParam !== moduleParam ||
      prevModule.filterModule !== filterModule ||
      prevKey !== filterModuleParamsKey;

    // Always update ref with current values
    prevModuleRef.current = { module, moduleParam, filterModule, filterModuleParams };

    // Only reset if module actually changed
    if (moduleChanged) {
      setPage(1);
      // Only reset totalCount if module actually changed
      setTotalCount(0);
    }
  }, [module, moduleParam, filterModule, filterModuleParams, filterModuleParamsKey]);

  const loadDocuments = useCallback(
    async (pageNum = 1, perPageNum = perPage) => {
      if (!module || !moduleParam) return;

      setIsLoading(true);
      try {
        const params = {
          module: module,
          module_param: moduleParam,
          page: pageNum,
          per_page: perPageNum,
          fields: "id,title,file,status,public,created_at,updated_at",
        };

        if (filterModule && filterModuleParamsKey) {
          params.filter_module = filterModule;
          params.filter_module_params = filterModuleParamsKey;
        }

        const response = await axios.get(`/documents`, { params });
        const loadedDocuments = response.data.documents || [];
        const apiTotalCount = response.data.totalCount || response.data.total || 0;

        const documentsToSet = loadedDocuments.map((doc) => ({
          ...doc,
          file: doc.file || null,
          title: doc.title || null,
        }));

        setDocuments(documentsToSet);
        setTotalCount(apiTotalCount);
        setDocumentCount(apiTotalCount);
        setPage(pageNum);

        // Optional parent sync (offer attachments AI panel, etc.). Default off so
        // paged loads do not overwrite checklist / other parents. Grid stays paged;
        // parent list needs every attachment, not only the current page.
        if (syncDocumentsToParent && typeof onDocumentsChange === "function") {
          let parentDocs = documentsToSet;
          if (apiTotalCount > documentsToSet.length) {
            const allParams = {
              ...params,
              page: 1,
              per_page: Math.min(Math.max(Number(apiTotalCount) || 0, 1), 500),
            };
            const allResponse = await axios.get(`/documents`, { params: allParams });
            const allLoaded = allResponse.data.documents || [];
            parentDocs = allLoaded.map((doc) => ({
              ...doc,
              file: doc.file || null,
              title: doc.title || null,
            }));
          }
          onDocumentsChange(parentDocs);
        }
      } catch (error) {
        console.error("Error loading documents:", error);
        toast.error("Kunne ikke laste dokumenter");
        if (compactDialogOpen && (mode === "mini" || mode === "dropzone") && showUpload) {
          setActiveTab("upload");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      module,
      moduleParam,
      filterModule,
      filterModuleParamsKey,
      perPage,
      onDocumentsChange,
      syncDocumentsToParent,
      compactDialogOpen,
      mode,
      showUpload,
    ]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      if (module && moduleParam) {
        loadDocuments(newPage, perPage);
      }
    },
    [module, moduleParam, perPage, loadDocuments]
  );

  // Load documents on mount when autoLoad is enabled (grid / inline modes)
  useEffect(() => {
    if (autoLoad && module && moduleParam && documents.length === 0) {
      loadDocuments(1, perPage);
    }
  }, [module, moduleParam, autoLoad, loadDocuments, perPage, documents.length]);

  // Paged load when compact dialog opens (mini / dropzone) — same API as grid mode
  useEffect(() => {
    if (compactDialogOpen && module && moduleParam && (mode === "mini" || mode === "dropzone")) {
      if (!hasLoadedOnDialogOpen.current) {
        hasLoadedOnDialogOpen.current = true;
        setPage(1);
        loadDocuments(1, perPage);
      }
    } else if (!compactDialogOpen) {
      hasLoadedOnDialogOpen.current = false;
    }
  }, [compactDialogOpen, mode, module, moduleParam, perPage, loadDocuments]);

  // Sync with props - but don't override if we've loaded documents ourselves
  useEffect(() => {
    // Mini/dropzone load paged data from API when the dialog opens
    if (mode === "mini" || mode === "dropzone") {
      return;
    }

    // Only sync if propsDocuments is explicitly provided and different
    // Don't override documents that were loaded via loadDocuments()
    // Skip if we've loaded documents on dialog open (hasLoadedOnDialogOpen.current)
    if (propsDocuments && Array.isArray(propsDocuments) && propsDocuments.length > 0) {
      // Check if documents are actually different (by ID or content)
      const documentsChanged = 
        propsDocuments.length !== documents.length ||
        propsDocuments.some((propDoc, index) => {
          const currentDoc = documents[index];
          return !currentDoc || propDoc.id !== currentDoc.id || 
                 propDoc.title !== currentDoc.title ||
                 propDoc.file?.filename !== currentDoc.file?.filename;
        });
      
      // Only update if documents actually changed, we're not loading, and we haven't just loaded documents ourselves
      if (documentsChanged && !isLoading && !hasLoadedOnDialogOpen.current) {
        setDocuments(propsDocuments);
        setDocumentCount(propsDocuments.length);
        setTotalCount(propsDocuments.length);
      }
    }
    // Don't clear documents if propsDocuments is empty/undefined - we may have loaded them ourselves
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsDocuments]); // Only depend on propsDocuments to prevent infinite loop

  // File selection handler - must be defined before drag/drop handlers
  const handleFileSelection = useCallback((event) => {
    // Handle both file input (event.target.files) and drag-and-drop (event.target.files or direct files array)
    const files = event.target?.files || (event.files ? Array.from(event.files) : []);
    const selectedFiles = Array.from(files);
    
    if (!selectedFiles.length) {
      return;
    }

    // Validate that we have module and moduleParam before proceeding
    if (!module || !moduleParam) {
      toast.error("Mangler modul eller modul parameter for opplasting");
      return;
    }

    const newFiles = selectedFiles.map((file) =>
      bindUploadEntryToFolder(
        {
          file,
          previewUrl: file.type === "application/pdf" ? null : URL.createObjectURL(file),
          title: file.name.replace(/\.[^/.]+$/, ""),
          module,
          moduleParam,
          drawing_id: defaultDrawing?.id || null,
          drawing_position_x: null,
          drawing_position_y: null,
        },
        currentFolder,
        fallbackModule,
        fallbackParam
      )
    );

    setFilesToUpload((prev) => [...prev, ...newFiles]);
    setUploadDialogOpen(true);
    
    // Reset file input so same file can be selected again
    if (event.target && event.target.value) {
      event.target.value = '';
    }
  }, [module, moduleParam, currentFolder, defaultDrawing?.id, fallbackModule, fallbackParam]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        // Create a FileList-like object for handleFileSelection
        handleFileSelection({ target: { files } });
      }
    },
    [handleFileSelection]
  );

  // Paste handler
  useEffect(() => {
    const handlePaste = (e) => {
      if (!isFocused) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")
      ) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        handleFileSelection({ target: { files } });
        setUploadDialogOpen(true);
      }
    };

    if (isFocused) {
      window.addEventListener("paste", handlePaste);
      return () => window.removeEventListener("paste", handlePaste);
    }
  }, [isFocused, handleFileSelection]);

  const handleTitleChange = (index, title) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].title = title;
      return updated;
    });
  };

  const handleDrawingChange = (index, drawing) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].drawing_id = drawing?.id || null;
      updated[index].drawing_position_x = null;
      updated[index].drawing_position_y = null;
      updated[index].drawing_snapshot_blob = null;
      return updated;
    });
  };

  const handlePositionChange = (index, position, extras = {}) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      if (extras?.wantSketchOnly) {
        updated[index].want_sketch = Boolean(position?.want_sketch ?? extras.want_sketch);
        return updated;
      }
      updated[index].drawing_position_x = position.x;
      updated[index].drawing_position_y = position.y;
      if (position?.rotation != null) {
        updated[index].drawing_rotation = position.rotation || 0;
      }
      if (position?.want_sketch != null) {
        updated[index].want_sketch = Boolean(position.want_sketch);
      }
      updated[index].drawing_snapshot_blob = extras?.snapshotBlob || null;
      return updated;
    });
  };

  const handleFilesRestoredFromPlacement = useCallback((files) => {
    setFilesToUpload(files);
    setUploadDialogOpen(true);
  }, []);

  // Restore upload dialog after full-canvas pin placement (mount + session complete).
  const placementRestoreTriedRef = useRef(false);
  useEffect(() => {
    const tryRestore = () => {
      if (placementRestoreTriedRef.current || !module || moduleParam == null) return;
      const peek = getDocumentPlacementSession();
      if (!peek || peek.source !== "document_manager") return;
      if (
        String(peek.module) !== String(module) ||
        String(peek.moduleParam) !== String(moduleParam)
      ) {
        return;
      }
      if (peek.status !== "completed" && peek.status !== "cancelled") return;
      const session = consumeDocumentPlacementRestore(module, moduleParam);
      if (!session) return;
      placementRestoreTriedRef.current = true;
      if (session.status === "completed" && session.files?.length) {
        handleFilesRestoredFromPlacement(session.files);
      }
    };
    tryRestore();
    return subscribeDocumentPlacement(tryRestore);
  }, [module, moduleParam, handleFilesRestoredFromPlacement]);

 

  const handleDeleteFile = (indexToDelete) => {
    setFilesToUpload((prev) => {
      const updated = prev.filter((_, index) => index !== indexToDelete);
      if (updated.length === 0) {
        setUploadDialogOpen(false);
      }
      return updated;
    });
  };

  const handleUploadComplete = (uploadedFiles) => {
    setFilesToUpload([]);
    setIsUploading(false);
    setUploadProgress({});
    setUploadedCount(0);
    setUploadDialogOpen(false);
    if (compactDialogOpen) {
      setActiveTab("view");
    }
    if (module && moduleParam) {
      setPage(1);
      void loadDocuments(1, perPage);
      void loadDocumentCount();
      if (onDocumentsChange && uploadedFiles?.length) {
        onDocumentsChange([...documents, ...uploadedFiles]);
      }
    } else {
      const newDocuments = [...documents, ...uploadedFiles];
      setDocuments(newDocuments);
      setDocumentCount(newDocuments.length);
      if (onDocumentsChange) {
        onDocumentsChange(newDocuments);
      }
    }
    toast.success(`${uploadedFiles.length} dokument(er) lastet opp`);
    if (resetUploader) {
      resetUploader();
    }
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => ({ ...prev, ...progress }));
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  const handleStartUpload = useCallback(async () => {
    if (!filesToUpload.length || isUploading) {
      return;
    }

    const destinationLabel = getDocumentUploadDestinationLabel(
      module,
      moduleParam,
      displaySubtitle,
      t
    );

    const toUpload = prepareUploadEntries(filesToUpload);
    if (!toUpload.length) {
      toast.error(t("documentUpload.bar.failed"));
      return;
    }

    if (documentUploadQueue) {
      setIsUploading(true);
      setUploadedCount(0);
      setUploadProgress({});
      setUploadDialogOpen(false);

      documentUploadQueue.enqueueUpload({
        files: toUpload,
        destinationLabel,
        module,
        moduleParam,
        onProgress: handleProgressChange,
        onFileComplete: handleFileUploadComplete,
        onComplete: handleUploadComplete,
        onError: () => {
          setIsUploading(false);
          toast.error(t("documentUpload.bar.failed"));
        },
      });
      return;
    }

    if (typeof uploadFunctionRef.current === "function") {
      setIsUploading(true);
      setUploadedCount(0);
      setUploadProgress({});
      try {
        await uploadFunctionRef.current();
      } catch (error) {
        console.error("Upload error:", error);
        setIsUploading(false);
        toast.error(t("documentUpload.bar.failed"));
      }
    }
  }, [
    filesToUpload,
    isUploading,
    module,
    moduleParam,
    displaySubtitle,
    t,
    documentUploadQueue,
    handleProgressChange,
    handleFileUploadComplete,
    handleUploadComplete,
  ]);

  const closeUploadDialog = useCallback(() => {
    setUploadDialogOpen(false);
    if (!isUploading) {
      setFilesToUpload([]);
    }
  }, [isUploading]);

  const handleFolderChange = (event) => {
    const folder = folderFromSelectorEvent(event);
    setCurrentFolder(folder);
    setFilesToUpload((prev) =>
      prev.map((entry) =>
        bindUploadEntryToFolder(entry, folder, fallbackModule, fallbackParam)
      )
    );
    if (onFolderChange) {
      onFolderChange(folder);
    }
  };

  // Document actions
  const handleDeleteDocument = async (document) => {
    try {
      await axios.delete(`/documents/${document.id}`);
      // Remove from local state immediately - don't reload to preserve other document data
      const newDocuments = documents.filter((d) => d.id !== document.id);
      const nextTotal = Math.max(0, totalCount - 1);
      setDocuments(newDocuments);
      setTotalCount(nextTotal);
      setDocumentCount(nextTotal);

      if (newDocuments.length === 0 && page > 1 && module && moduleParam) {
        await loadDocuments(page - 1, perPage);
      } else if (onDocumentsChange && propsDocuments?.length > 0) {
        onDocumentsChange(propsDocuments.filter((d) => d.id !== document.id));
      }

      toast.success("Dokument slettet");
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Kunne ikke slette dokument");
      if (module && moduleParam) {
        await loadDocuments(page, perPage);
        await loadDocumentCount();
      }
    }
  };

  const handleEditDocument = (document) => {
    setSelectedDocument(document);
    setEditDialogOpen(true);
  };

  const handleDocumentUpdated = (updatedDocument) => {
    const newDocuments = documents.map((d) =>
      d.id === updatedDocument.id ? updatedDocument : d
    );
    setDocuments(newDocuments);
    if (onDocumentsChange) {
      onDocumentsChange(newDocuments);
    }
    setEditDialogOpen(false);
    setSelectedDocument(null);
  };

  // Copy/Move handlers
  const handleCopyDocuments = async (selectedDocuments) => {
    if (!module || !moduleParam) {
      toast.error("Må ha modul og modul parameter for å kopiere");
      return;
    }

    try {
      // Call API to copy documents
      const response = await axios.post(`/documents/copy`, {
        document_ids: selectedDocuments.map((d) => d.id),
        target_module: module,
        target_module_param: moduleParam,
      });

      const copiedDocuments = response.data.documents || [];
      const newDocuments = [...documents, ...copiedDocuments];
      setDocuments(newDocuments);
      
      if (onDocumentsChange) {
        onDocumentsChange(newDocuments);
      }
      
      toast.success(`${copiedDocuments.length} dokument(er) kopiert`);
      
      if (module && moduleParam) {
        setPage(1);
        await loadDocuments(1, perPage);
        await loadDocumentCount();
      }
    } catch (error) {
      console.error("Error copying documents:", error);
      const errorMessage = error?.response?.data?.error || "Kunne ikke kopiere dokumenter";
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleMoveDocuments = async (selectedDocuments) => {
    if (!module || !moduleParam) {
      toast.error("Må ha modul og modul parameter for å flytte");
      return;
    }

    try {
      // Call API to move documents
      const response = await axios.post(`/documents/move`, {
        document_ids: selectedDocuments.map((d) => d.id),
        target_module: module,
        target_module_param: moduleParam,
      });

      const movedDocuments = response.data.documents || [];
      const newDocuments = [...documents, ...movedDocuments];
      setDocuments(newDocuments);
      
      if (onDocumentsChange) {
        onDocumentsChange(newDocuments);
      }
      
      toast.success(`${movedDocuments.length} dokument(er) flyttet`);
      
      if (module && moduleParam) {
        setPage(1);
        await loadDocuments(1, perPage);
        await loadDocumentCount();
      }
    } catch (error) {
      console.error("Error moving documents:", error);
      const errorMessage = error?.response?.data?.error || "Kunne ikke flytte dokumenter";
      toast.error(errorMessage);
      throw error;
    }
  };

  // Image viewer - calculate image URLs and map document indices
  const { imageUrls, documentToImageIndex } = useMemo(() => {
    const urls = [];
    const indexMap = {};
    let imageCount = 0;
    
    documents.forEach((doc, docIndex) => {
      const filename = doc?.file?.filename?.toLowerCase() || "";
      const isPdf = filename.endsWith(".pdf");
      const hasUrl = doc?.file?.url || doc?.file?.thumb?.url;
      
      if (!isPdf && hasUrl) {
        urls.push(doc?.file?.url || doc?.file?.thumb?.url);
        indexMap[docIndex] = imageCount;
        imageCount++;
      }
    });
    
    return { imageUrls: urls.filter(Boolean), documentToImageIndex: indexMap };
  }, [documents]);

  // Image viewer
  const openImageViewer = useCallback((documentIndex) => {
    const imageIndex = documentToImageIndex[documentIndex] ?? 0;
    setCurrentImageIndex(imageIndex);
    setViewerOpen(true);
  }, [documentToImageIndex]);

  const closeImageViewer = () => {
    setViewerOpen(false);
    setCurrentImageIndex(0);
  };


  // Render modes
  const renderCompactMode = () => {
    const visibleDocuments = documents.slice(0, maxDisplay);
    const hiddenCount = documents.length - maxDisplay;

    return (
      <div className="flex flex-wrap gap-2">
        {visibleDocuments.map((document, index) => (
          <div
            key={document.id}
            className="relative group w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-all cursor-pointer"
            onClick={() => openImageViewer(index)}
          >
            <DocumentImageOptimized
              pathType="quickview"
              document={document}
              fallback={
                document?.file?.type === "application/pdf" ? PdfPlaceholder : null
              }
            />
            {allowDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Er du sikker på at du vil slette dette dokumentet?")) {
                    handleDeleteDocument(document);
                  }
                }}
                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500"
          >
            <span className="text-xs">+{hiddenCount}</span>
          </button>
        )}
        {showUpload && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500"
          >
            <CameraIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  };

  // Ultra-compact mode for checklist items - just button with count
  const renderMiniMode = () => {
    const docCount =
      totalCount > 0 ? totalCount : documentCount > 0 ? documentCount : documents.length;

    return (
      <button
        type="button"
        onClick={() => {
          // Set active tab based on whether documents exist
          // If no documents, show upload tab, otherwise show view tab
          const initialTab = docCount === 0 && showUpload ? "upload" : "view";
          setActiveTab(initialTab);
          setCompactDialogOpen(true);
        }}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed h-8 min-w-[2rem]"
        title={docCount > 0 ? `${docCount} dokument${docCount !== 1 ? 'er' : ''}` : "Ingen dokumenter"}
      >
        <PhotoIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
        <span className="text-xs font-medium text-gray-700 bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center flex-shrink-0">
          {docCount}
        </span>
      </button>
    );
  };

  const renderGridMode = () => {
    // Debug: Log document structure when rendering
    if (documents.length > 0 && documents[0]) {
      console.log("DocumentManagerV6 - renderGridMode - First document:", {
        id: documents[0].id,
        title: documents[0].title,
        file: documents[0].file,
        fileFilename: documents[0].file?.filename,
        fullDocument: documents[0]
      });
    }
    
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {documents.map((document, index) => {
            // Debug each document
            const displayTitle = document?.title || document?.file?.filename || "Uten tittel";
            const displayFilename = document?.file?.filename || document?.file?.name || document?.document_file_name || "Ukjent fil";

            
            return (
            <div
              key={document.id}
              className="group relative bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all overflow-hidden"
            >
              <div
                className="aspect-square cursor-pointer"
                onClick={() => {
                  if (document?.file?.filename?.toLowerCase().endsWith(".pdf")) {
                    window.open(document?.file?.url, "_blank");
                  } else {
                    openImageViewer(index);
                  }
                }}
              >
                <DocumentImageOptimized
                  pathType="quickview"
                  document={document}
                  fallback={
                    document?.file?.type === "application/pdf" ? PdfPlaceholder : null
                  }
                />
              </div>
              <div className="p-2">
                <p className="text-xs font-medium text-gray-900 truncate" title={displayTitle}>
                  {displayTitle}
                </p>
                <p className="text-xs text-gray-500 truncate" title={displayFilename}>
                  {displayFilename}
                </p>
              </div>
              {(allowEdit || allowDelete) && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {allowEdit && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDocument(document);
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1.5"
                      title="Rediger"
                    >
                      <PencilIcon className="h-3 w-3" />
                    </button>
                  )}
                  {allowDelete && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Er du sikker på at du vil slette dette dokumentet?")) {
                          handleDeleteDocument(document);
                        }
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                      title="Slett"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
        {totalCount > perPage && (
          <PMPagination
            currentCount={documents.length}
            totalCount={totalCount}
            page={page}
            onPageChange={handlePageChange}
            perPage={perPage}
            showCount={true}
            showPages={true}
            showNextPrev={true}
          />
        )}
      </div>
    );
  };

  const renderInlineMode = () => {
    return (
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={TWclassNames(
          "border-2 border-dashed rounded-lg p-4 transition-all",
          isDragging
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50",
          "cursor-pointer"
        )}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
          <PhotoIcon className="h-8 w-8" />
          <p className="text-sm font-medium text-center">
            {isDragging ? "Slipp bildet her" : "Dra og slipp bilde eller klikk for å velge"}
          </p>
          <p className="text-xs text-gray-400">Eller trykk Ctrl+V for å lime inn</p>
        </div>
      </div>
    );
  };

  // Dropzone mode with single "Last opp/ Vis bilder" button
  const renderDropzoneMode = () => {
    // Use totalCount if available (from loaded documents), otherwise use documentCount (preloaded status)
    // If documents are loaded, use totalCount to show total number of documents
    // If documents are not loaded yet, use documentCount for status display
    const docCount = totalCount > 0 ? totalCount : (documents.length > 0 ? documents.length : (documentCount || 0));
    
    return (
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={t("documentUpload.dropzone.ariaLabel")}
        className={TWclassNames(
          "w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all relative",
          isDragging
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 bg-white hover:border-blue-400 hover:bg-gray-50",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        )}
        onClick={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          
          // If clicking on the button area, open dialog
          // If dragging files, handle via onDrop
          if (!isDragging) {
            // Set active tab based on whether documents exist
            const initialTab = docCount === 0 && showUpload ? "upload" : "view";
            setActiveTab(initialTab);
            setCompactDialogOpen(true);
            // Documents will be loaded via useEffect when dialog opens
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedFileTypes}
          multiple
          className="sr-only"
          onChange={handleFileSelection}
          style={{ 
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            borderWidth: 0,
          }}
          tabIndex={-1}
        />
        <PhotoIcon className="h-5 w-5 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">
          {isLoadingCount ? (
            t("documentUpload.dropzone.loading")
          ) : docCount > 0 ? (
            t("documentUpload.dropzone.viewImagesCount", { count: docCount })
          ) : (
            t("documentUpload.dropzone.viewImages")
          )}
        </span>
        {isLoadingCount && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 ml-2"></div>
        )}
      </div>
    );
  };

  // Main render
  if (mode === "dropzone") {
    return (
      <>
        {renderDropzoneMode()}
        {compactDialogOpen && (
          <CompactDialog
            key={`compact-dialog-${documents.length}`}
            open={compactDialogOpen}
            onClose={() => {
              setCompactDialogOpen(false);
              setActiveTab("view");
            }}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            documents={documents}
            isLoading={isLoading}
            module={module}
            moduleParam={moduleParam}
            folderSupport={folderSupport}
            currentFolder={currentFolder}
            onFolderChange={handleFolderChange}
            showUpload={showUpload}
            allowCopy={allowCopy}
            allowMove={allowMove}
            allowDelete={allowDelete}
            allowEdit={allowEdit}
            disabled={disabled}
            onDocumentsChange={onDocumentsChange}
            onDocumentDelete={handleDeleteDocument}
            onDocumentEdit={handleEditDocument}
            onImageViewerOpen={openImageViewer}
            fileInputRef={fileInputRef}
            filesToUpload={filesToUpload}
            setFilesToUpload={setFilesToUpload}
            uploadDialogOpen={uploadDialogOpen}
            setUploadDialogOpen={setUploadDialogOpen}
            handleFileSelection={handleFileSelection}
            handleTitleChange={handleTitleChange}
            handleDeleteFile={handleDeleteFile}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            uploadedCount={uploadedCount}
            uploadFunctionRef={uploadFunctionRef}
            handleUploadComplete={handleUploadComplete}
            handleProgressChange={handleProgressChange}
            handleFileUploadComplete={handleFileUploadComplete}
            resetUploader={resetUploader}
            setResetUploader={setResetUploader}
            allowedFileTypes={allowedFileTypes}
            handleCopyDocuments={handleCopyDocuments}
            handleMoveDocuments={handleMoveDocuments}
            setIsUploading={setIsUploading}
            setUploadedCount={setUploadedCount}
            setUploadProgress={setUploadProgress}
            filterModule={filterModule}
            filterModuleParams={filterModuleParams}
            checklistId={checklistId}
            checklistItemIds={checklistItemIds}
            page={page}
            perPage={perPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            displayTitle={"Dokumenter"}
            displaySubtitle={displaySubtitle}
            onDrawingChange={handleDrawingChange}
            onPositionChange={handlePositionChange}
            project={project}
            defaultDrawing={defaultDrawing}
            lockDrawing={lockDrawing}
            autoOpenPositionPicker={autoOpenPositionPicker}
            enableSketch={enableSketch}
            handleStartUpload={handleStartUpload}
            closeUploadDialog={closeUploadDialog}
            placementRestoreTriedRef={placementRestoreTriedRef}
            t={t}
          />
        )}
        <UploaderV4
          filesToUpload={filesToUpload}
          onUploadStart={(uploadFn) => {
            if (uploadFn && typeof uploadFn === "function") {
              uploadFunctionRef.current = uploadFn;
              bumpUploadFnReady();
            }
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
        />
        {viewerOpen && imageUrls.length > 0 && currentImageIndex < imageUrls.length && (
          <ImageViewer
            src={imageUrls}
            currentIndex={currentImageIndex}
            disableScroll={false}
            closeOnClickOutside={true}
            onClose={closeImageViewer}
            backgroundStyle={{
              backgroundColor: "rgba(0,0,0,0.9)",
            }}
          />
        )}
        {editDialogOpen && selectedDocument && (
          <EditDocumentDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedDocument(null);
            }}
            document={selectedDocument}
            onRefresh={handleDocumentUpdated}
            editPublic={false}
          />
        )}
      </>
    );
  }

  if (mode === "mini") {
    return (
      <>
        {renderMiniMode()}
        {compactDialogOpen && (
          <CompactDialog
            key={`compact-dialog-${documents.length}`}
            open={compactDialogOpen}
            onClose={() => {
              setCompactDialogOpen(false);
              setActiveTab("view");
            }}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            documents={documents}
            isLoading={isLoading}
            module={module}
            moduleParam={moduleParam}
            folderSupport={folderSupport}
            currentFolder={currentFolder}
            onFolderChange={handleFolderChange}
            showUpload={showUpload}
            allowCopy={allowCopy}
            allowMove={allowMove}
            allowDelete={allowDelete}
            allowEdit={allowEdit}
            disabled={disabled}
            onDocumentsChange={onDocumentsChange}
            onDocumentDelete={handleDeleteDocument}
            onDocumentEdit={handleEditDocument}
            onImageViewerOpen={openImageViewer}
            fileInputRef={fileInputRef}
            filesToUpload={filesToUpload}
            setFilesToUpload={setFilesToUpload}
            uploadDialogOpen={uploadDialogOpen}
            setUploadDialogOpen={setUploadDialogOpen}
            handleFileSelection={handleFileSelection}
            handleTitleChange={handleTitleChange}
            handleDeleteFile={handleDeleteFile}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            uploadedCount={uploadedCount}
            uploadFunctionRef={uploadFunctionRef}
            handleUploadComplete={handleUploadComplete}
            handleProgressChange={handleProgressChange}
            handleFileUploadComplete={handleFileUploadComplete}
            resetUploader={resetUploader}
            setResetUploader={setResetUploader}
            allowedFileTypes={allowedFileTypes}
            handleCopyDocuments={handleCopyDocuments}
            handleMoveDocuments={handleMoveDocuments}
            setIsUploading={setIsUploading}
            setUploadedCount={setUploadedCount}
            setUploadProgress={setUploadProgress}
            filterModule={filterModule}
            filterModuleParams={filterModuleParams}
            checklistId={checklistId}
            checklistItemIds={checklistItemIds}
            page={page}
            perPage={perPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            displayTitle={"Dokumenter"}
            displaySubtitle={displaySubtitle}
            onDrawingChange={handleDrawingChange}
            onPositionChange={handlePositionChange}
            project={project}
            defaultDrawing={defaultDrawing}
            lockDrawing={lockDrawing}
            autoOpenPositionPicker={autoOpenPositionPicker}
            enableSketch={enableSketch}
            handleStartUpload={handleStartUpload}
            closeUploadDialog={closeUploadDialog}
            placementRestoreTriedRef={placementRestoreTriedRef}
            t={t}
          />
        )}
        <UploaderV4
          filesToUpload={filesToUpload}
          onUploadStart={(uploadFn) => {
            if (uploadFn && typeof uploadFn === "function") {
              uploadFunctionRef.current = uploadFn;
              bumpUploadFnReady();
            }
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
        />
        {viewerOpen && imageUrls.length > 0 && currentImageIndex < imageUrls.length && (
          <ImageViewer
            src={imageUrls}
            currentIndex={currentImageIndex}
            disableScroll={false}
            closeOnClickOutside={true}
            onClose={closeImageViewer}
            backgroundStyle={{
              backgroundColor: "rgba(0,0,0,0.9)",
            }}
          />
        )}
        {editDialogOpen && selectedDocument && (
          <EditDocumentDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedDocument(null);
            }}
            document={selectedDocument}
            onRefresh={handleDocumentUpdated}
            editPublic={false}
          />
        )}
        {/* Hidden file input for dropzone mode */}
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedFileTypes}
          multiple
          className="sr-only"
          onChange={handleFileSelection}
        />
      </>
    );
  }

  if (mode === "inline") {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedFileTypes}
          multiple
          className="sr-only"
          onChange={handleFileSelection}
        />
        <div className="space-y-3">
          {showExisting && (
            <div>
              {isLoading ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-8 text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                  <p className="mt-2 text-sm text-gray-500">Laster dokumenter...</p>
                </div>
              ) : documents.length > 0 ? (
                renderGridMode()
              ) : null}
            </div>
          )}
          {showUpload && !disabled ? renderInlineMode() : null}
        </div>
        {uploadDialogOpen && (
          <UploadDialog
            open={uploadDialogOpen}
            onClose={closeUploadDialog}
            onStartUpload={handleStartUpload}
            placementRestoreTriedRef={placementRestoreTriedRef}
            t={t}
            enableSketch={enableSketch}
            filesToUpload={filesToUpload}
            onTitleChange={handleTitleChange}
            onDeleteFile={handleDeleteFile}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            uploadedCount={uploadedCount}
            filesToUploadLength={filesToUpload.length}
            module={module}
            moduleParam={moduleParam}
            folderSupport={folderSupport}
            currentFolder={currentFolder}
            onFolderChange={handleFolderChange}
            uploadFunctionRef={uploadFunctionRef}
            onUploadStart={(fn) => {
              uploadFunctionRef.current = fn;
            }}
            onUploadComplete={handleUploadComplete}
            onProgressChange={handleProgressChange}
            onFileUploadComplete={handleFileUploadComplete}
            onReset={setResetUploader}
            fileInputRef={fileInputRef}
            handleFileSelection={handleFileSelection}
            onDrawingChange={handleDrawingChange}
            onPositionChange={handlePositionChange}
            project={project}
            defaultDrawing={defaultDrawing}
            lockDrawing={lockDrawing}
            autoOpenPositionPicker={autoOpenPositionPicker}
          />
        )}
        <UploaderV4
          filesToUpload={filesToUpload}
          onUploadStart={(uploadFn) => {
            if (uploadFn && typeof uploadFn === "function") {
              uploadFunctionRef.current = uploadFn;
              bumpUploadFnReady();
            }
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
        />
        {editDialogOpen && selectedDocument && (
          <EditDocumentDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedDocument(null);
            }}
            document={selectedDocument}
            onRefresh={handleDocumentUpdated}
            editPublic={false}
          />
        )}
        {viewerOpen && imageUrls.length > 0 && currentImageIndex < imageUrls.length && (
          <ImageViewer
            src={imageUrls}
            currentIndex={currentImageIndex}
            disableScroll={false}
            closeOnClickOutside={true}
            onClose={closeImageViewer}
            backgroundStyle={{
              backgroundColor: "rgba(0,0,0,0.9)",
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Dokumenter ({totalCount > 0 ? totalCount : documents.length})
        </h3>
        <div className="flex gap-2">
          {showUpload && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <CameraIcon className="h-4 w-4 mr-2" />
              Last opp
            </button>
          )}
          {(allowCopy || allowMove) && (
            <button
              type="button"
              onClick={() => setCopyMoveDialogOpen(true)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
              Kopier/Flytt
            </button>
          )}
        </div>
      </div>

      {/* Documents display */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Laster dokumenter...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Ingen dokumenter</p>
          {showUpload && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
            >
              <CameraIcon className="h-4 w-4 mr-2" />
              Last opp første dokument
            </button>
          )}
        </div>
      ) : (
        <>
          {mode === "compact" && renderCompactMode()}
          {mode === "grid" && renderGridMode()}
        </>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFileTypes}
        multiple
        className="sr-only"
        onChange={handleFileSelection}
      />

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <UploadDialog
          open={uploadDialogOpen}
          onClose={closeUploadDialog}
          onStartUpload={handleStartUpload}
          placementRestoreTriedRef={placementRestoreTriedRef}
          t={t}
          enableSketch={enableSketch}
          filesToUpload={filesToUpload}
          onTitleChange={handleTitleChange}
          onDeleteFile={handleDeleteFile}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          uploadedCount={uploadedCount}
          filesToUploadLength={filesToUpload.length}
          module={module}
          moduleParam={moduleParam}
          folderSupport={folderSupport}
          currentFolder={currentFolder}
          onFolderChange={handleFolderChange}
          uploadFunctionRef={uploadFunctionRef}
          onUploadStart={(fn) => {
            uploadFunctionRef.current = fn;
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
          fileInputRef={fileInputRef}
          handleFileSelection={handleFileSelection}
          onDrawingChange={handleDrawingChange}
          onPositionChange={handlePositionChange}
          project={project}
          defaultDrawing={defaultDrawing}
          lockDrawing={lockDrawing}
          autoOpenPositionPicker={autoOpenPositionPicker}
        />
      )}

      {/* Copy/Move Dialog - TODO: Implement */}
      {copyMoveDialogOpen && (
        <PMDialog
          open={copyMoveDialogOpen}
          onClose={() => setCopyMoveDialogOpen(false)}
          title="Kopier eller flytt dokumenter"
          size="4xl"
        >
          <div className="p-4">
            <p className="text-gray-600">Funksjonalitet kommer snart...</p>
            {/* TODO: Implement document picker with copy/move functionality */}
          </div>
        </PMDialog>
      )}

      {/* Edit Dialog */}
      {editDialogOpen && selectedDocument && (
        <EditDocumentDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          onRefresh={handleDocumentUpdated}
          editPublic={false}
        />
      )}

      {/* Image Viewer */}
      {viewerOpen && imageUrls.length > 0 && currentImageIndex < imageUrls.length && (
        <ImageViewer
          src={imageUrls}
          currentIndex={currentImageIndex}
          disableScroll={false}
          closeOnClickOutside={true}
          onClose={closeImageViewer}
          backgroundStyle={{
            backgroundColor: "rgba(0,0,0,0.9)",
          }}
        />
      )}

      {/* Uploader Component */}
        <UploaderV4
          filesToUpload={filesToUpload}
          onUploadStart={(uploadFn) => {
            if (uploadFn && typeof uploadFn === "function") {
              uploadFunctionRef.current = uploadFn;
              bumpUploadFnReady();
            }
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
        />
    </div>
  );
};

// Upload Dialog Component
const UploadDialog = ({
  open,
  onClose,
  filesToUpload,
  onTitleChange,
  onDeleteFile,
  isUploading,
  uploadProgress,
  uploadedCount,
  filesToUploadLength,
  module,
  moduleParam,
  folderSupport,
  currentFolder,
  onFolderChange,
  uploadFunctionRef,
  onUploadStart,
  onStartUpload,
  onUploadComplete,
  onProgressChange,
  onFileUploadComplete,
  onReset,
  fileInputRef,
  handleFileSelection,
  onDrawingChange,
  onPositionChange,
  project, // Project prop passed from DocumentManagerV6
  defaultDrawing = null,
  lockDrawing = false,
  autoOpenPositionPicker = false,
  placementRestoreTriedRef,
  enableSketch = false,
  t,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawings, setDrawings] = useState({});
  const autoOpenedRef = useRef(new Set());
  const filesRef = useRef(filesToUpload);
  filesRef.current = filesToUpload;

  useEffect(() => {
    if (!defaultDrawing?.id || !filesToUpload?.length) return;
    setDrawings((prev) => {
      const next = { ...prev };
      filesToUpload.forEach((_, index) => {
        if (!next[index]) next[index] = defaultDrawing;
      });
      return next;
    });
    filesToUpload.forEach((file, index) => {
      if (!file.drawing_id && !file.drawing_pin_declined) {
        onDrawingChange(index, defaultDrawing);
      }
    });
  }, [defaultDrawing?.id, filesToUpload?.length]);

  const startFullCanvasPlacement = useCallback(
    (index) => {
      if (isUploading) return;
      const files = filesRef.current || [];
      const file = files[index];
      if (!file) return;
      const drawing =
        drawings[index] ||
        (file.drawing_id
          ? defaultDrawing?.id === file.drawing_id
            ? defaultDrawing
            : { id: file.drawing_id }
          : defaultDrawing);
      if (!drawing?.id) {
        toast.error(
          t?.("documentUpload.preview.selectDrawingFirst") || "Velg tegning først"
        );
        return;
      }
      const mode = requestFullCanvasDocumentPlacement({
        drawingId: drawing.id,
        fileIndex: index,
        files: files.map((entry) => ({ ...entry })),
        module,
        moduleParam,
        wantSketch: Boolean(file.want_sketch),
        returnPath: sanitizeReturnPath(locationToPath(location)),
        title: file.title || file.file?.name || "",
        source: "document_manager",
        navigate,
      });
      if (!mode) {
        toast.error(
          t?.("documentUpload.preview.drawingLoadFailed") || "Kunne ikke åpne tegning"
        );
        return;
      }
      // New placement session started — allow restore effect to run again when it completes.
      if (placementRestoreTriedRef?.current != null) {
        placementRestoreTriedRef.current = false;
      }
      autoOpenedRef.current.add(index);
      onClose?.();
    },
    [
      drawings,
      defaultDrawing,
      isUploading,
      location.pathname,
      location.search,
      module,
      moduleParam,
      navigate,
      onClose,
      t,
    ]
  );

  useEffect(() => {
    if (!autoOpenPositionPicker || !defaultDrawing?.id || isUploading || !open) return;
    const session = getDocumentPlacementSession();
    if (session?.status === "placing") return;
    const index = (filesToUpload || []).findIndex(
      (file, i) =>
        (file.drawing_id || drawings[i]?.id || defaultDrawing.id) &&
        (file.drawing_position_x == null || file.drawing_position_y == null) &&
        !autoOpenedRef.current.has(i) &&
        !shouldSkipAutoOpenPlacement(module, moduleParam, i)
    );
    if (index < 0) return;
    startFullCanvasPlacement(index);
  }, [
    autoOpenPositionPicker,
    defaultDrawing?.id,
    isUploading,
    open,
    filesToUpload,
    drawings,
    module,
    moduleParam,
    startFullCanvasPlacement,
  ]);

  const handleDrawingSelect = (index, drawing) => {
    if (lockDrawing) return;
    onDrawingChange(index, drawing);
    setDrawings((prev) => ({ ...prev, [index]: drawing }));
  };

  const handleOpenPositionPicker = (index) => {
    startFullCanvasPlacement(index);
  };

  const handleWantSketchChange = (index, checked) => {
    onPositionChange?.(index, {
      x: filesToUpload[index]?.drawing_position_x,
      y: filesToUpload[index]?.drawing_position_y,
      rotation: filesToUpload[index]?.drawing_rotation || 0,
      want_sketch: checked,
    }, { wantSketchOnly: true });
  };

  const handleAddMoreFiles = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.click();
    }
  };

  const handleConfirmUpload = () => {
    if (isUploading) {
      return;
    }
    if (onStartUpload) {
      void onStartUpload();
    } else if (typeof uploadFunctionRef?.current === "function") {
      void uploadFunctionRef.current().catch((error) => {
        console.error("Upload error:", error);
        toast.error(t?.("documentUpload.bar.failed") || "Feil ved opplasting");
      });
    }
  };

  const renderBeforeList = () => {
    const folderProjectId =
      module === "project" ? moduleParam : project?.id;
    if (!folderSupport || !folderProjectId) {
      return null;
    }
    return (
      <div className="mb-3 flex-shrink-0 rounded-lg bg-blue-50 p-3 ring-1 ring-inset ring-blue-100 sm:p-4">
        <div className="mb-2 flex items-center">
          <FolderIcon className="mr-2 h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Mappe:</span>
        </div>
        <ProjectFolderSelector
          projectId={folderProjectId}
          selected={currentFolder}
          onChange={onFolderChange}
          zIndex={DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX}
        />
      </div>
    );
  };

  const renderFileExtra = (file, index) => {
    if (!project) {
      return null;
    }
    const selectedDrawing =
      drawings[index] ||
      (file.drawing_id
        ? defaultDrawing?.id === file.drawing_id
          ? defaultDrawing
          : { id: file.drawing_id }
        : null);
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {t?.("documentUpload.preview.drawingLabel") || "Tegning (valgfritt)"}
        </label>
        {lockDrawing && selectedDrawing ? (
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            {selectedDrawing.title ||
              t?.("documentUpload.preview.linkedDrawing", { id: selectedDrawing.id }) ||
              `Tegning #${selectedDrawing.id}`}
            <p className="mt-0.5 text-xs text-blue-700">
              {t?.("documentUpload.preview.linkedDrawingHint") ||
                "Sjekklisten er knyttet til denne tegningen — plasser pin."}
            </p>
          </div>
        ) : (
          <DrawingSelector
            projectId={project?.id || (module === "project" ? moduleParam : null)}
            selected={selectedDrawing}
            onChange={(drawing) => handleDrawingSelect(index, drawing)}
            placeholder={t?.("documentUpload.preview.drawingPlaceholder") || "Velg tegning..."}
            disabled={isUploading}
            zIndex={DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX}
          />
        )}
        {selectedDrawing && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t?.("documentUpload.preview.drawingPositionLabel") || "Posisjon på tegning"}
            </label>
            {file.drawing_position_x !== null && file.drawing_position_y !== null ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">
                  X: {Number(file.drawing_position_x).toFixed(2)}, Y:{" "}
                  {Number(file.drawing_position_y).toFixed(2)}
                </span>
                {file.drawing_snapshot_blob ? (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    {t?.("documentUpload.preview.sketchAttached") || "Skisse lagt til"}
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleOpenPositionPicker(index)}
                  disabled={isUploading}
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t?.("documentUpload.preview.changeDrawingPosition") || "Endre posisjon"}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleOpenPositionPicker(index)}
                disabled={isUploading}
                className="w-full rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t?.("documentUpload.preview.pickDrawingPosition") || "Velg posisjon på tegning"}
              </button>
            )}
            {enableSketch ? (
              <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(file.want_sketch)}
                  onChange={(e) => handleWantSketchChange(index, e.target.checked)}
                  disabled={isUploading}
                />
                <span>
                  <span className="font-medium">
                    {t?.("documentUpload.preview.enableSketch") || "Markering / skisse på tegning"}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {t?.("documentUpload.preview.enableSketchHintFullCanvas") ||
                      "Valgfritt: etter at du klikker på tegningen åpnes skisse på full tegning."}
                  </span>
                </span>
              </label>
            ) : null}
            <p className="text-xs text-gray-500">
              {t?.("documentUpload.preview.fullCanvasPlacementHint") ||
                "Pin plasseres på den faktiske tegningen (full viewer)."}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderAfterList = () => (
    <>
      {isUploading && (
        <>
          <div className="mt-4 h-4 w-full rounded bg-gray-200">
            <div
              className="h-full rounded bg-emerald-500 transition-all duration-300"
              style={{
                width: `${
                  (Object.values(uploadProgress).reduce((acc, val) => acc + val, 0) /
                    (filesToUploadLength * 100)) *
                  100
                }%`,
              }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-emerald-800">
            {t?.("documentUpload.bar.continueHint") ||
              "Opplastingen fortsetter øverst på siden — du kan lukke dette vinduet."}
          </p>
        </>
      )}

      {isUploading && (
        <p className="mt-2 text-center text-sm text-gray-700">
          Opplasting {uploadedCount + 1} av {filesToUploadLength}
        </p>
      )}

      {uploadedCount === filesToUploadLength && isUploading && (
        <div className="mt-4 flex items-center justify-center text-green-500">
          <CheckCircleIcon className="h-6 w-6" />
          <span className="ml-2 font-semibold text-green-600">Opplasting fullført</span>
        </div>
      )}
    </>
  );

  return (
    <DocumentUploadPreviewDialog
      open={open}
      onClose={onClose}
      filesToUpload={filesToUpload}
      onTitleChange={onTitleChange}
      onDeleteFile={onDeleteFile}
      onAddMore={handleAddMoreFiles}
      onConfirm={handleConfirmUpload}
      isUploading={isUploading}
      confirmDisabled={!onStartUpload && !uploadFunctionRef?.current}
      renderBeforeList={renderBeforeList}
      renderAfterList={renderAfterList}
      renderFileExtra={project ? renderFileExtra : undefined}
      supportPdf
    />
  );
};

// Compact Dialog with Tabs Component
const CompactDialog = ({
  open,
  onClose,
  activeTab,
  onTabChange,
  documents,
  isLoading = false,
  module,
  moduleParam,
  folderSupport,
  currentFolder,
  onFolderChange,
  showUpload,
  allowCopy,
  allowMove,
  allowDelete,
  allowEdit,
  disabled,
  onDocumentsChange,
  onDocumentDelete,
  onDocumentEdit,
  onImageViewerOpen,
  fileInputRef,
  filesToUpload,
  setFilesToUpload,
  uploadDialogOpen,
  setUploadDialogOpen,
  handleFileSelection,
  handleTitleChange,
  handleDeleteFile,
  isUploading,
  uploadProgress,
  uploadedCount,
  uploadFunctionRef,
  handleUploadComplete,
  handleProgressChange,
  handleFileUploadComplete,
  resetUploader,
  setResetUploader,
  allowedFileTypes,
  handleCopyDocuments,
  handleMoveDocuments,
  setIsUploading,
  setUploadedCount,
  setUploadProgress,
  filterModule,
  filterModuleParams = EMPTY_PROPS_ARRAY,
  page = 1,
  perPage = 10,
  totalCount = 0,
  onPageChange,
  displayTitle,
  displaySubtitle,
  onDrawingChange,
  onPositionChange,
  project,
  defaultDrawing = null,
  lockDrawing = false,
  autoOpenPositionPicker = false,
  enableSketch = false,
  handleStartUpload,
  closeUploadDialog,
  placementRestoreTriedRef,
  t,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropZoneRef = useRef(null);
  /** Ref alone does not re-render UploadDialog; bump when UploaderV4 registers the handler. */
  const [, bumpUploadFnReadyCompact] = useReducer((x) => x + 1, 0);

  // Determine which tabs to show
  const tabs = [];
  tabs.push({ id: "view", label: "Vis dokumenter" });
  if (showUpload) tabs.push({ id: "upload", label: "Last opp" });
  if (allowCopy || allowMove) tabs.push({ id: "copyMove", label: "Kopier/Flytt" });

  // Drag and drop handlers for upload tab
  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelection({ target: { files } });
        setUploadDialogOpen(true);
      }
    },
    [handleFileSelection, setUploadDialogOpen]
  );

  // Paste handler
  useEffect(() => {
    const handlePaste = (e) => {
      if (!isFocused || activeTab !== "upload") return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")
      ) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        handleFileSelection({ target: { files } });
        setUploadDialogOpen(true);
      }
    };

    if (isFocused && activeTab === "upload") {
      window.addEventListener("paste", handlePaste);
      return () => window.removeEventListener("paste", handlePaste);
    }
  }, [isFocused, disabled, activeTab, handleFileSelection, setUploadDialogOpen]);

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="Dokumenter"
      dense
      panelClassName={DOCUMENT_MANAGER_COMPACT_PANEL}
    >
      <div className="flex h-full min-h-0 w-full flex-1 flex-col px-1 sm:px-0">
      {/* Subtitle */}
      {displaySubtitle && (
        <div className="mb-3 flex-shrink-0 border-b border-gray-200 pb-2">
          <p className="text-md font-medium text-gray-600">{displaySubtitle}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-3 flex-shrink-0 border-b border-gray-200">
        <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTabChange(tab.id);
              }}
              className={TWclassNames(
                "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 cursor-pointer"
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content — flex child scrolls inside fixed-height dense dialog */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* View Tab */}
        {activeTab === "view" && (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-500">Laster dokumenter...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <PhotoIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Ingen dokumenter</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 pb-2">
                {documents.map((document, index) => (
                  <div
                    key={document.id}
                    className="group relative bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all overflow-hidden"
                  >
                    <div
                      className="aspect-square cursor-pointer"
                      onClick={() => {
                        if (document?.file?.filename?.toLowerCase().endsWith(".pdf")) {
                          window.open(document?.file?.url, "_blank");
                        } else {
                          onImageViewerOpen(index);
                        }
                      }}
                    >
                      <DocumentImageOptimized
                        pathType="quickview"
                        document={document}
                        fallback={
                          document?.file?.type === "application/pdf" ? PdfPlaceholder : null
                        }
                      />
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-900 truncate" title={document?.title || document?.file?.filename || "Uten tittel"}>
                        {document?.title || document?.file?.filename || "Uten tittel"}
                      </p>
                      <p className="text-xs text-gray-500 truncate" title={document?.file?.filename || document?.file?.name || document?.document_file_name || "Ukjent fil"}>
                        {document?.file?.filename || document?.file?.name || document?.document_file_name || "Ukjent fil"}
                      </p>
                    </div>
                    {(allowEdit || allowDelete) && !disabled && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {allowEdit && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDocumentEdit(document);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1.5"
                            title="Rediger"
                          >
                            <PencilIcon className="h-3 w-3" />
                          </button>
                        )}
                        {allowDelete && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm("Er du sikker på at du vil slette dette dokumentet?")) {
                                onDocumentDelete(document);
                              }
                            }}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                            title="Slett"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>
            {/* Pagination pinned below scroll area */}
            {!isLoading && totalCount > perPage && (
              <div className="flex-shrink-0 border-t border-gray-200 bg-white px-1 py-3">
                <p className="mb-2 text-center text-xs text-gray-500">
                  {t?.("documentUpload.viewer.showing", {
                    from: (page - 1) * perPage + 1,
                    to: Math.min(page * perPage, totalCount),
                    total: totalCount,
                    defaultValue: `Viser ${(page - 1) * perPage + 1}–${Math.min(page * perPage, totalCount)} av ${totalCount}`,
                  })}
                </p>
                <PMPagination
                  currentCount={documents.length}
                  totalCount={totalCount}
                  page={page}
                  onPageChange={onPageChange}
                  perPage={perPage}
                  showCount={true}
                  showPages={true}
                  showNextPrev={true}
                />
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === "upload" && (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain">
            {/* Drop Zone */}
            <div
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={TWclassNames(
                "border-2 border-dashed rounded-lg p-6 sm:p-8 transition-all text-center min-h-[min(40vh,280px)] flex items-center justify-center",
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50",
                disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              )}
              onClick={() => !disabled && fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
                <PhotoIcon className="h-12 w-12" />
                <p className="text-sm font-medium">
                  {isDragging ? "Slipp bildet her" : "Dra og slipp bilde eller klikk for å velge"}
                </p>
                <p className="text-xs text-gray-400">Eller trykk Ctrl+V for å lime inn</p>
              </div>
            </div>

            {/* File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedFileTypes}
              multiple
              className="sr-only"
              onChange={handleFileSelection}
            />

            {/* Upload Dialog */}
            {uploadDialogOpen && (
              <UploadDialog
                open={uploadDialogOpen}
                onClose={closeUploadDialog}
                onStartUpload={handleStartUpload}
                placementRestoreTriedRef={placementRestoreTriedRef}
                t={t}
                enableSketch={enableSketch}
                filesToUpload={filesToUpload}
                onTitleChange={handleTitleChange}
                onDeleteFile={handleDeleteFile}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                uploadedCount={uploadedCount}
                filesToUploadLength={filesToUpload.length}
                module={module}
                moduleParam={moduleParam}
                folderSupport={folderSupport}
                currentFolder={currentFolder}
                onFolderChange={onFolderChange}
                uploadFunctionRef={uploadFunctionRef}
                onUploadStart={() => {}}
                onUploadComplete={handleUploadComplete}
                onProgressChange={handleProgressChange}
                onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
          fileInputRef={fileInputRef}
          handleFileSelection={handleFileSelection}
          onDrawingChange={onDrawingChange}
          onPositionChange={onPositionChange}
          project={project}
          defaultDrawing={defaultDrawing}
          lockDrawing={lockDrawing}
          autoOpenPositionPicker={autoOpenPositionPicker}
        />
      )}

      {/* Uploader Component */}
            <UploaderV4
              filesToUpload={filesToUpload}
              onUploadStart={(uploadFn) => {
                if (uploadFn && typeof uploadFn === "function") {
                  uploadFunctionRef.current = uploadFn;
                  bumpUploadFnReadyCompact();
                }
              }}
              onUploadComplete={handleUploadComplete}
              onProgressChange={handleProgressChange}
              onFileUploadComplete={handleFileUploadComplete}
              onReset={setResetUploader}
            />
          </div>
        )}

        {/* Copy/Move Tab */}
        {activeTab === "copyMove" && (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <CopyMoveTab
              module={module}
              moduleParam={moduleParam}
              filterModule={filterModule}
              filterModuleParams={filterModuleParams}
              allowCopy={allowCopy}
              allowMove={allowMove}
              disabled={disabled}
              onCopy={handleCopyDocuments}
              onMove={handleMoveDocuments}
            />
          </div>
        )}
      </div>
      </div>
    </PMDialog>
  );
};

// Copy/Move Tab Component
const CopyMoveTab = ({
  module: targetModule,
  moduleParam: targetModuleParam,
  filterModule,
  filterModuleParams = EMPTY_PROPS_ARRAY,
  checklistId = null,
  checklistItemIds = EMPTY_PROPS_ARRAY,
  allowCopy,
  allowMove,
  disabled,
  onCopy,
  onMove,
}) => {
  const [sourceModule, setSourceModule] = useState(filterModule || "report");
  const getInitialModuleParam = () => {
    if (filterModule && filterModuleParams.length > 0) {
      return filterModuleParams[0].toString();
    }
    return "";
  };
  const [sourceModuleParam, setSourceModuleParam] = useState(getInitialModuleParam());
  const [searchQuery, setSearchQuery] = useState("");
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [moduleParams, setModuleParams] = useState([]);
  const [isLoadingParams, setIsLoadingParams] = useState(false);
  const [showReportSelector, setShowReportSelector] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Get default module param from filterModuleParams if available
  const getDefaultModuleParam = useCallback(() => {
    // If source module matches filter module, use filterModuleParams as default
    if (sourceModule && filterModule === sourceModule && filterModuleParams.length > 0) {
      return filterModuleParams[0].toString();
    }
    return "";
  }, [sourceModule, filterModule, filterModuleParams]);

  // Available modules for selection
  const availableModules = [
    { value: "report", label: "Rapport" },
    { value: "checklist_item", label: "Sjekkliste punkt" },
    { value: "case", label: "Case" },
    { value: "project", label: "Prosjekt" },
    { value: "task", label: "Oppgave" },
    { value: "meeting_minute", label: "Møtereferat" },
  ];

  // Load module params when module changes
  useEffect(() => {
    if (sourceModule && sourceModule !== "report" && sourceModule !== "project" && sourceModule !== "case") {
      loadModuleParams();
    } else {
      setModuleParams([]);
      const defaultParam = getDefaultModuleParam();
      setSourceModuleParam(defaultParam);
      setSelectedReport(null);
      setSelectedProject(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceModule, getDefaultModuleParam]);

  const [showCaseSelector, setShowCaseSelector] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  // Update sourceModuleParam when selectedReport, selectedProject, or selectedCase changes
  useEffect(() => {
    if (selectedReport) {
      setSourceModuleParam(selectedReport.id.toString());
    } else if (selectedProject) {
      setSourceModuleParam(selectedProject.id.toString());
    } else if (selectedCase) {
      setSourceModuleParam(selectedCase.id.toString());
      // Trigger search immediately when case is selected
      if (sourceModule === "case") {
        setTimeout(() => {
          searchDocuments();
        }, 100);
      }
    } else if (sourceModule === "report" || sourceModule === "project" || sourceModule === "case") {
      // Use default from props if available
      const defaultParam = getDefaultModuleParam();
      if (defaultParam) {
        setSourceModuleParam(defaultParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, selectedProject, selectedCase, sourceModule, getDefaultModuleParam]);

  // Initialize sourceModuleParam with default when sourceModule changes
  useEffect(() => {
    const defaultParam = getDefaultModuleParam();
    if (defaultParam && !sourceModuleParam) {
      setSourceModuleParam(defaultParam);
    }
  }, [sourceModule, getDefaultModuleParam]);

  const loadModuleParams = async () => {
    setIsLoadingParams(true);
    try {
      // Use documents API to get unique module_params for the selected module
      // This gives us a list of available items that have documents
      const response = await axios.get(`/documents`, {
        params: {
          module: sourceModule,
          per_page: 1000, // Get many to find unique module_params
          fields: 'module_param',
        },
      });
      
      // Extract unique module_params from documents
      const uniqueParams = [...new Set((response.data.documents || []).map(doc => doc.module_param).filter(Boolean))];
      
      // For checklist_item, we need to get the actual checklist item data
      // But since we don't have a direct API, we'll just use the IDs
      if (sourceModule === "checklist_item" && checklistId) {
        // Filter to only include items from this checklist
        // The API should already filter this, but we'll double-check
        setModuleParams(uniqueParams.map(param => ({ id: param, title: `Item #${param}` })));
      } else {
        // For other modules, create simple objects with id
        setModuleParams(uniqueParams.map(param => ({ id: param, title: param })));
      }
    } catch (error) {
      console.error("Error loading module params:", error);
      setModuleParams([]);
    } finally {
      setIsLoadingParams(false);
    }
  };

  // Search for documents
  const searchDocuments = async () => {
    if (!sourceModule) return;

    setIsLoading(true);
    try {
      const params = {
        search: searchQuery || undefined,
      };

      // Always use module_param if set, otherwise use default from props
      const effectiveModuleParam = sourceModuleParam || getDefaultModuleParam();
      
      // For report and project modules, always require module_param (security)
      if (sourceModule === "report" || sourceModule === "project") {
        if (!effectiveModuleParam) {
          const moduleName = sourceModule === "report" ? "rapport" : "prosjekt";
          toast.error(`Må velge en ${moduleName} for å se dokumenter`);
          setAvailableDocuments([]);
          setIsLoading(false);
          return;
        }
        params.module = sourceModule;
        params.module_param = effectiveModuleParam;
      } else if (sourceModule === "checklist_item") {
        // For checklist_item, we can use either checklistId, reportId, or a specific item ID
        params.module = "checklist_item";
        
        // If a specific item is selected (sourceModuleParam is set and not empty)
        if (effectiveModuleParam && effectiveModuleParam !== "" && effectiveModuleParam !== "Alle") {
          params.module_param = effectiveModuleParam;
        }
        // If reportId is available (from filterModuleParams), use report_checklist_items filter
        else if (filterModule === "report" && filterModuleParams.length > 0) {
          // Use report_checklist_items filter to get all checklist items from the report
          // The API expects filters in format: filters[0][name] and filters[0][value]
          const reportId = filterModuleParams[0].toString();
          // Serialize filters correctly for Rails nested params
          params["filters[0][name]"] = "report_checklist_items";
          params["filters[0][value]"] = reportId;
          // Filter by item IDs client-side after getting the response
        } 
        // If checklistId is available, use checklist filter
        else if (checklistId) {
          // Use checklist filter to get items from a specific checklist
          params["filters[0][name]"] = "checklist";
          params["filters[0][value]"] = checklistId.toString();
          // Filter by item IDs client-side after getting the response
        }
        // If "Alle" is selected and we have checklistItemIds, filter client-side
        else if (checklistItemIds.length > 0) {
          // Just get all checklist_item documents, we'll filter by item IDs client-side
          // No filter needed - will get all and filter client-side
        } else {
          toast.error("Må ha sjekkliste ID, rapport ID, eller velge et spesifikt punkt for å se dokumenter");
          setAvailableDocuments([]);
          setIsLoading(false);
          return;
        }
      } else if (sourceModule === "case") {
        // For case, we MUST select a case (cannot show all case items)
        // Documents for case are linked to case items (module: 'CASE_ITEM')
        // Use case_items filter to get all case items from the selected case
        if (!effectiveModuleParam) {
          toast.error("Må velge en case for å se dokumenter");
          setAvailableDocuments([]);
          setIsLoading(false);
          return;
        }
        const caseId = effectiveModuleParam.toString();
        // Use case_items filter to get all case items from the case
        params["filters[0][name]"] = "case_items";
        params["filters[0][value]"] = caseId;
      } else {
        // For other modules, set module and module_param if available
        params.module = sourceModule;
        if (effectiveModuleParam) {
          params.module_param = effectiveModuleParam;
        }
      }

      // Build query string manually to handle nested params like filters[0][name]
      const queryParams = new URLSearchParams();
      
      // Add all regular params (not starting with filters[)
      Object.keys(params).forEach(key => {
        if (!key.startsWith('filters[')) {
          if (params[key] !== undefined && params[key] !== null) {
            queryParams.append(key, params[key]);
          }
        }
      });
      
      // Add filters manually in Rails nested format
      if (params["filters[0][name]"] && params["filters[0][value]"]) {
        queryParams.append('filters[0][name]', params["filters[0][name]"]);
        queryParams.append('filters[0][value]', params["filters[0][value]"]);
      }
      
      const queryString = queryParams.toString();
      const url = queryString ? `/documents?${queryString}` : `/documents`;
      
      const response = await axios.get(url);
      
      // For checklist_item, filter client-side to only include documents from specified item IDs
      if (sourceModule === "checklist_item" && checklistItemIds.length > 0) {
        const itemIdSet = new Set(checklistItemIds.map(id => id.toString()));
        const filteredDocuments = (response.data.documents || []).filter(doc => 
          doc.module === "checklist_item" && itemIdSet.has(doc.module_param)
        );
        setAvailableDocuments(filteredDocuments);
      } else {
        setAvailableDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error("Error searching documents:", error);
      toast.error("Kunne ikke søke etter dokumenter");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize checklistItemIds to prevent infinite loops
  const checklistItemIdsString = useMemo(() => {
    if (!checklistItemIds || checklistItemIds.length === 0) return '';
    return [...checklistItemIds].sort().join(',');
  }, [checklistItemIds]);

  // Debounced search - only run when sourceModule is set and conditions are met
  useEffect(() => {
    // Don't search if sourceModule is not set
    if (!sourceModule) return;

    // For checklist_item, allow search if:
    // - A specific item is selected (sourceModuleParam is set)
    // - reportId is available (filterModule === "report")
    // - checklistId is available
    // - checklistItemIds is available (for "Alle" option)
    if (sourceModule === "checklist_item") {
      const hasSpecificItem = sourceModuleParam && sourceModuleParam !== "" && sourceModuleParam !== "Alle";
      const hasReportId = filterModule === "report" && filterModuleParams.length > 0;
      const hasChecklistId = checklistId;
      const hasChecklistItemIds = checklistItemIds.length > 0;
      
      // Only block if none of the conditions are met
      if (!hasSpecificItem && !hasReportId && !hasChecklistId && !hasChecklistItemIds) {
        return;
      }
    }

    // For report/project/case, don't search if module_param is missing
    const defaultParam = getDefaultModuleParam();
    if ((sourceModule === "report" || sourceModule === "project" || sourceModule === "case") && !sourceModuleParam && !defaultParam) {
      return;
    }

    const timer = setTimeout(() => {
      searchDocuments();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceModule, sourceModuleParam, searchQuery, checklistId, checklistItemIdsString]);

  const toggleDocumentSelection = (document) => {
    setSelectedDocuments((prev) => {
      const isSelected = prev.some((d) => d.id === document.id);
      if (isSelected) {
        return prev.filter((d) => d.id !== document.id);
      } else {
        return [...prev, document];
      }
    });
  };

  const handleCopy = async () => {
    if (selectedDocuments.length === 0) {
      toast.error("Velg minst ett dokument");
      return;
    }

    setIsProcessing(true);
    try {
      await onCopy(selectedDocuments);
      setSelectedDocuments([]);
    } catch (error) {
      console.error("Error copying documents:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMove = async () => {
    if (selectedDocuments.length === 0) {
      toast.error("Velg minst ett dokument");
      return;
    }

    if (
      !window.confirm(
        `Er du sikker på at du vil flytte ${selectedDocuments.length} dokument(er)?`
      )
    ) {
      return;
    }

    setIsProcessing(true);
    try {
      await onMove(selectedDocuments);
      setSelectedDocuments([]);
    } catch (error) {
      console.error("Error moving documents:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Source Module Selection */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Fra modul
        </label>
        <select
          value={sourceModule}
          onChange={(e) => {
            setSourceModule(e.target.value);
            setSourceModuleParam("");
            setAvailableDocuments([]);
          }}
          disabled={disabled || isProcessing}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {availableModules.map((mod) => (
            <option key={mod.value} value={mod.value}>
              {mod.label}
            </option>
          ))}
        </select>
      </div>

      {/* Report Selection */}
      {sourceModule === "report" && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Rapport <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowReportSelector(true)}
              disabled={disabled || isProcessing}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedReport ? (
                <span className="text-sm text-gray-900">
                  {selectedReport.displayTitle || `Rapport #${selectedReport.id}`}
                </span>
              ) : sourceModuleParam ? (
                <span className="text-sm text-gray-600">
                  Rapport #{sourceModuleParam} (fra kontekst)
                </span>
              ) : (
                <span className="text-sm text-gray-500">Velg rapport...</span>
              )}
            </button>
            {(selectedReport || sourceModuleParam) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedReport(null);
                  // Reset to default from props if available
                  const defaultParam = getDefaultModuleParam();
                  setSourceModuleParam(defaultParam);
                }}
                disabled={disabled || isProcessing || !!getDefaultModuleParam()}
                className="px-3 py-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  getDefaultModuleParam() 
                    ? "Kan ikke fjerne - bruker rapport fra kontekst" 
                    : "Fjern valg"
                }
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {selectedReport 
              ? `Viser dokumenter fra valgt rapport`
              : sourceModuleParam
              ? `Viser dokumenter fra rapport #${sourceModuleParam} (fra kontekst)`
              : "Må velge en rapport for å se dokumenter"}
          </p>
        </div>
      )}

      {/* Project Selection */}
      {sourceModule === "project" && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Prosjekt <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowProjectSelector(true)}
              disabled={disabled || isProcessing}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedProject ? (
                <span className="text-sm text-gray-900">
                  {selectedProject.displayTitle || selectedProject.name || `Prosjekt #${selectedProject.id}`}
                </span>
              ) : sourceModuleParam ? (
                <span className="text-sm text-gray-600">
                  Prosjekt #{sourceModuleParam} (fra kontekst)
                </span>
              ) : (
                <span className="text-sm text-gray-500">Velg prosjekt...</span>
              )}
            </button>
            {(selectedProject || sourceModuleParam) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedProject(null);
                  // Reset to default from props if available
                  const defaultParam = getDefaultModuleParam();
                  setSourceModuleParam(defaultParam);
                }}
                disabled={disabled || isProcessing || !!getDefaultModuleParam()}
                className="px-3 py-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  getDefaultModuleParam() 
                    ? "Kan ikke fjerne - bruker prosjekt fra kontekst" 
                    : "Fjern valg"
                }
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {selectedProject 
              ? `Viser dokumenter fra valgt prosjekt`
              : sourceModuleParam
              ? `Viser dokumenter fra prosjekt #${sourceModuleParam} (fra kontekst)`
              : "Må velge et prosjekt for å se dokumenter"}
          </p>
        </div>
      )}

      {/* Case Selection */}
      {sourceModule === "case" && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Case <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowCaseSelector(true)}
              disabled={disabled || isProcessing}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedCase ? (
                <span className="text-sm text-gray-900">
                  {selectedCase.displayTitle || `Case #${selectedCase.id}`}
                </span>
              ) : sourceModuleParam ? (
                <span className="text-sm text-gray-600">
                  Case #{sourceModuleParam} (fra kontekst)
                </span>
              ) : (
                <span className="text-sm text-gray-500">Velg case...</span>
              )}
            </button>
            {(selectedCase || sourceModuleParam) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedCase(null);
                  // Reset to default from props if available
                  const defaultParam = getDefaultModuleParam();
                  setSourceModuleParam(defaultParam);
                }}
                disabled={disabled || isProcessing || !!getDefaultModuleParam()}
                className="px-3 py-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  getDefaultModuleParam() 
                    ? "Kan ikke fjerne - bruker case fra kontekst" 
                    : "Fjern valg"
                }
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {selectedCase 
              ? `Viser dokumenter fra valgt case`
              : sourceModuleParam
              ? `Viser dokumenter fra case #${sourceModuleParam} (fra kontekst)`
              : "Må velge en case for å se dokumenter"}
          </p>
        </div>
      )}

      {/* Module Param Selection (if needed) */}
      {sourceModule !== "report" && sourceModule !== "project" && sourceModule !== "case" && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {availableModules.find((m) => m.value === sourceModule)?.label}
            {getDefaultModuleParam() && (
              <span className="text-xs text-gray-500 ml-1">(fra kontekst: {getDefaultModuleParam()})</span>
            )}
          </label>
          <select
            value={sourceModuleParam}
            onChange={(e) => {
              setSourceModuleParam(e.target.value);
            }}
            disabled={disabled || isProcessing || isLoadingParams}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {getDefaultModuleParam() ? (
              <option value={getDefaultModuleParam()}>
                Bruk fra kontekst ({getDefaultModuleParam()})
              </option>
            ) : (
              <option value="">Alle</option>
            )}
            {moduleParams.map((param) => (
              <option key={param.id} value={param.id}>
                {param.title || param.name || `ID: ${param.id}`}
              </option>
            ))}
          </select>
          {getDefaultModuleParam() && !sourceModuleParam && (
            <p className="text-xs text-gray-500">
              Bruker {sourceModule} #{getDefaultModuleParam()} fra kontekst
            </p>
          )}
        </div>
      )}

      {/* Search */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Søk
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Søk etter dokumenter..."
          disabled={disabled || isProcessing}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Selected Count */}
      {selectedDocuments.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            {selectedDocuments.length} dokument(er) valgt
          </p>
        </div>
      )}

      {/* Documents Grid */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">
            Tilgjengelige dokumenter
          </h3>
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          )}
        </div>

         {((sourceModule === "report" || sourceModule === "project" || sourceModule === "case") && !sourceModuleParam && !getDefaultModuleParam()) ? (
           <div className="text-center py-8 text-gray-500">
             <PhotoIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
             <p>
               {sourceModule === "report" 
                 ? "Velg en rapport for å se dokumenter"
                 : sourceModule === "project"
                 ? "Velg et prosjekt for å se dokumenter"
                 : "Velg en case for å se dokumenter"}
             </p>
           </div>
         ) : availableDocuments.length === 0 && !isLoading && ((sourceModule !== "report" && sourceModule !== "project" && sourceModule !== "case") || sourceModuleParam || getDefaultModuleParam()) ? (
          <div className="text-center py-8 text-gray-500">
            <PhotoIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p>Ingen dokumenter funnet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
            {availableDocuments.map((document) => {
              const isSelected = selectedDocuments.some(
                (d) => d.id === document.id
              );
              return (
                <div
                  key={document.id}
                  className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    isSelected
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                  onClick={() => !disabled && !isProcessing && toggleDocumentSelection(document)}
                >
                  <div className="aspect-square">
                    <DocumentImageOptimized
                      pathType="quickview"
                      document={document}
                      fallback={
                        document?.file?.type === "application/pdf"
                          ? PdfPlaceholder
                          : null
                      }
                    />
                  </div>
                  {isSelected && (
                    <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1">
                      <CheckCircleIcon className="h-4 w-4" />
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-900 truncate" title={document?.title || document?.file?.filename || "Uten tittel"}>
                      {document?.title || document?.file?.filename || "Uten tittel"}
                    </p>
                    <p className="text-xs text-gray-500 truncate" title={document?.file?.filename || document?.file?.name || document?.document_file_name || "Ukjent fil"}>
                      {document?.file?.filename || document?.file?.name || document?.document_file_name || "Ukjent fil"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t">
        {allowCopy && (
          <button
            type="button"
            onClick={handleCopy}
            disabled={disabled || isProcessing || selectedDocuments.length === 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Kopierer..." : `Kopier (${selectedDocuments.length})`}
          </button>
        )}
        {allowMove && (
          <button
            type="button"
            onClick={handleMove}
            disabled={disabled || isProcessing || selectedDocuments.length === 0}
            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Flytter..." : `Flytt (${selectedDocuments.length})`}
          </button>
        )}
      </div>

      {/* Report Selector Modal */}
      {showReportSelector && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowReportSelector(false)} 
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl h-[80vh] flex flex-col">
              <ModernReportSelector
                open={showReportSelector}
                selectedReports={selectedReport ? [selectedReport] : []}
                onChange={(reports) => {
                  if (reports.length > 0) {
                    setSelectedReport(reports[0]);
                    setSourceModuleParam(reports[0].id.toString());
                  } else {
                    setSelectedReport(null);
                    setSourceModuleParam("");
                  }
                  setShowReportSelector(false);
                }}
                onClose={() => setShowReportSelector(false)}
                multiple={false}
                showHeader={true}
              />
            </div>
          </div>
        </div>
      )}

       {/* Project Selector Modal */}
       {showProjectSelector && (
         <div className="fixed inset-0 z-50 overflow-y-auto">
           <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
             <div 
               className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
               onClick={() => setShowProjectSelector(false)} 
             />
             <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl h-[80vh] flex flex-col">
               <ModernProjectSelector
                 open={showProjectSelector}
                 selectedProjects={selectedProject ? [selectedProject] : []}
                 onChange={(projects) => {
                   if (projects.length > 0) {
                     setSelectedProject(projects[0]);
                     setSourceModuleParam(projects[0].id.toString());
                   } else {
                     setSelectedProject(null);
                     setSourceModuleParam("");
                   }
                   setShowProjectSelector(false);
                 }}
                 onClose={() => setShowProjectSelector(false)}
                 multiple={false}
                 showHeader={true}
               />
             </div>
           </div>
         </div>
       )}

       {/* Case Selector Modal */}
       {showCaseSelector && (
         <div className="fixed inset-0 z-50 overflow-y-auto">
           <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
             <div 
               className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
               onClick={() => setShowCaseSelector(false)} 
             />
             <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl h-[80vh] flex flex-col">
               <div className="p-4 border-b">
                 <h3 className="text-lg font-medium text-gray-900">Velg case</h3>
               </div>
               <div className="flex-1 overflow-y-auto p-4">
                 <CaseSelector
                   open={showCaseSelector}
                   selected={selectedCase ? { id: selectedCase.id, field1: selectedCase.displayTitle } : null}
                   onChange={(event) => {
                     // CaseSelector sends data.selected.item which contains { id, value, primary, secondary, object }
                     // The object property contains the actual case object
                     const rowParams = event?.target?.data;
                     if (rowParams?.object) {
                       // We have the full case object
                       const caseData = rowParams.object;
                       setSelectedCase(caseData);
                       setSourceModuleParam(caseData.id.toString());
                       setShowCaseSelector(false);
                     } else if (event?.target?.value) {
                       // If we only have the ID, fetch the case
                       const caseId = event.target.value;
                       if (caseId) {
                         axios.get(`/cases/${caseId}`)
                           .then((res) => {
                             if (res.data) {
                               setSelectedCase(res.data);
                               setSourceModuleParam(res.data.id.toString());
                             }
                             setShowCaseSelector(false);
                           })
                           .catch((err) => {
                             console.error("Error fetching case:", err);
                             toast.error("Kunne ikke hente case");
                             setShowCaseSelector(false);
                           });
                       } else {
                         setSelectedCase(null);
                         setSourceModuleParam("");
                         setShowCaseSelector(false);
                       }
                     } else {
                       setSelectedCase(null);
                       setSourceModuleParam("");
                       setShowCaseSelector(false);
                     }
                   }}
                 />
               </div>
               <div className="p-4 border-t flex justify-end">
                 <button
                   type="button"
                   onClick={() => setShowCaseSelector(false)}
                   className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                 >
                   Lukk
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

